import { ITypedFormData, TModelRequestParameters, mergePaths } from '@cian/http-api';
import { IConfig } from '@cian/config/shared';
import { TAddTemporarilyModel } from '../../common/repositories/encrypted-file-manager/v2/add-temporarily';

export type TFormDataConstructor = new () => FormData;

export function convertObjectToFormData<TRequest extends TModelRequestParameters<TRequest>>(
  object: TRequest | FormData,
  Ctr?: TFormDataConstructor,
): ITypedFormData<TModelRequestParameters<TRequest>> {
  if (checkIsFormData(object)) {
    return object as ITypedFormData<TModelRequestParameters<TRequest>>;
  }
  const form = Ctr ? new Ctr() : new FormData();

  // eslint-disable-next-line guard-for-in
  for (const key in object) {
    form.append(key, object[key]);
  }

  return form as ITypedFormData<TModelRequestParameters<TRequest>>;
}

function checkIsFormData(object: Record<string, unknown> | FormData): object is FormData {
  return object.constructor?.name === 'FormData';
}

interface IParams {
  model: TAddTemporarilyModel;
  config: IConfig;
  subdomain?: string;
}

export function createURL(params: IParams): URL {
  const { model, subdomain, config } = params;

  const baseUrl = getBaseUrlFromConfig(config, model);
  const url = new URL(baseUrl);

  if (subdomain) {
    url.hostname = `${subdomain}.${url.hostname}`;
  }

  url.pathname = mergePaths(url.pathname, model.pathApi);

  return url;
}

function getBaseUrlFromConfig(config: IConfig, model: TAddTemporarilyModel) {
  const { hostType = 'api', microserviceName } = model;
  const mcsRunEnvType = config.get<string>('mcsRunEnvType');
  const hostPart = hostType === 'ud-api' ? 'udApiBrowserUrl' : 'browserUrl';

  let baseServiceUri = config.get<string>(`${microserviceName}.${hostPart}`);

  if (baseServiceUri) {
    return baseServiceUri;
  }

  const microserviceNameCamelCase = kebabCaseToCamelCase(microserviceName);
  baseServiceUri = config.get<string>(`${microserviceNameCamelCase}.${hostPart}`);

  if (baseServiceUri) {
    return baseServiceUri;
  }

  const microserviceNameKebabCase = camelCaseToKebabCase(microserviceName);
  const mcsRunEnv = config.getStrict<string>('mcsRunEnv');

  switch (mcsRunEnvType) {
    case 'dev':
    case 'local':
      return `https://master.api.dev3.cian.ru/${microserviceNameKebabCase}/`;

    case 'beta':
      return `https://api.${mcsRunEnv.replace('-', '')}.stage.cian.ru/${microserviceNameKebabCase}/`;

    default:
      return `https://${hostType}.cian.ru/${microserviceNameKebabCase}/`;
  }
}

function kebabCaseToCamelCase(str: string) {
  return str
    .split('-')
    .map((word: string, index: number) => (index ? capitalizeFirstChar(word) : word))
    .join('');
}

function camelCaseToKebabCase(str: string) {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function capitalizeFirstChar(str: string) {
  if (!str) {
    return str;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const firstChar = str[0]!.toUpperCase();

  return `${firstChar}${str.slice(1)}`;
}
